import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import FormField from './../FormField';
import VenueField from './../VenueField';
import { connect } from 'react-redux';
import { withGoogleMap, GoogleMap, Marker, Circle } from 'react-google-maps';
import {Card, CardBody, TabContent, TabPane, Nav, NavLink} from 'reactstrap';
import styles from './LocationFieldset.scss';
import { DesktopMediaQuery, MobileMediaQuery } from '../../MediaQueries';
import VenueFieldSet from './VenueFieldset';
import { checkEventEligibility } from 'actions/publishersActions';

const prepareCatchments = (catchments) => {
  let catchmentsToDisplay = [];
  if (!catchments){
    return catchmentsToDisplay;
  }

  for (const [key, catchment] of catchments.entries()) {
    catchmentsToDisplay.push({
      key: key,
      radius: catchment.get('radius') * 1000,
      lat: catchment.get('latitude'),
      lng: catchment.get('longitude')
    });
  }

  return catchmentsToDisplay;
};

export const MapPreview = withGoogleMap(({ venue, online, catchments }) => {
  const mapPosition = { lat: venue.lat, lng: venue.lng };

  const zoom = online ? (venue.town ? 8 : (venue.state && venue.state.code ? 5 : 3) ) : 15;

  const catchmentsToDisplay = prepareCatchments(catchments);
  const catchmentCircleOptions = {
    fillColor: 'rgba(62, 109, 202, 0.4)',
    strokeColor: '#328BA9',
    strokeOpacity: 0.8,
    strokeWeight: 1
  };
  return (
    <GoogleMap
      defaultZoom={zoom}
      center={mapPosition}
      defaultCenter={mapPosition}
    >
      <Marker position={mapPosition}/>
      {catchmentsToDisplay.map(catchment => {
        return (
          <Fragment key={catchment.key}>
            <Marker
              position={{
                lat: parseFloat(catchment.lat),
                lng: parseFloat(catchment.lng),
              }}
              options={{visible: false}}
            />
            <Circle
              defaultCenter={{
                lat: parseFloat(catchment.lat),
                lng: parseFloat(catchment.lng)
              }}
              radius={catchment.radius}
              options={catchmentCircleOptions}
            />
          </Fragment>
        );
      })}
    </GoogleMap>
  );
});

const VenueDetailsFieldset = (props) => {
  if (props.venue === undefined || (!props.venue.place_id && !props.venue.name)) { return null; }
  const mapVisible = window.google && props.venue.lat && props.venue.lng;
  const originPublisherName = props.originPublisherData && props.originPublisherData.get('name');
  const locationCatchments = props.originPublisherData && props.originPublisherData.get('catchments');

  return (
    <div className="mb-4">
      <MobileMediaQuery>
        <div>
          <Nav tabs>
            <NavLink
              className={props.currentTab === 'address' ? 'active' : ''}
              onClick={() => props.onTabChange('address')}
            >
              Address
            </NavLink>
            <NavLink
              className={props.currentTab === 'map' ? 'active' : ''}
              onClick={() => props.onTabChange('map')}
            >
              Map
            </NavLink>
          </Nav>
          <Card className="card--with-tabs">
            <CardBody>
              <TabContent activeTab={props.currentTab}>
                <TabPane tabId="address">
                  <div className="row">
                    <div className="col condensed-form-group">
                      <VenueFieldSet
                        formErrors={props.formErrors}
                        availableCountries={props.availableCountries}
                        onChangeCountry={props.onChangeCountry}
                        onChangeZip={props.onChangeZip}
                        onClear={props.onClear}
                        onEdit={props.onEdit}
                        editMode={props.editMode}
                        venue={props.venue}
                        originPublisherEligibility={props.originPublisherEligibility}
                        originPublisherName={originPublisherName}
                      />
                    </div>
                  </div>
                </TabPane>
                <TabPane tabId="map">
                  <div className="row">
                    <div className="col">
                      {mapVisible &&
                        <MapPreview
                          key={`google-map-${props.currentTab}`}
                          containerElement={<div style={{ height: '500px' }} />}
                          mapElement={<div style={{ height: '500px' }} />}
                          venue={props.venue}
                          online={props.online}
                          catchments={locationCatchments}
                        />
                      }
                    </div>
                  </div>
                </TabPane>
              </TabContent>
            </CardBody>
          </Card>
        </div>
      </MobileMediaQuery>
      <DesktopMediaQuery>
        <Card>
          <CardBody className={styles.bodyWithBtn}>
            <div className="row">
              <div className="col condensed-form-group">
                <VenueFieldSet
                  formErrors={props.formErrors}
                  availableCountries={props.availableCountries}
                  onChangeCountry={props.onChangeCountry}
                  onChangeZip={props.onChangeZip}
                  onClear={props.onClear}
                  onEdit={props.onEdit}
                  editMode={props.editMode}
                  venue={props.venue}
                  originPublisherEligibility={props.originPublisherEligibility}
                  originPublisherName={originPublisherName}
                />
              </div>
              <div className="col">
                {mapVisible &&
                  <MapPreview
                    containerElement={<div style={{ height: '100%' }} />}
                    mapElement={<div style={{ height: '100%' }} />}
                    venue={props.venue}
                    online={props.online}
                    catchments={locationCatchments}
                  />
                }
              </div>
            </div>
          </CardBody>
        </Card>
      </DesktopMediaQuery>
    </div>
  );
};

class LocationFieldset extends Component {
  static propTypes = {
    formErrors: PropTypes.object,
    venue: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    onClearAddress: PropTypes.func.isRequired,
    availableCountries: PropTypes.object.isRequired,
    onVenueSelect: PropTypes.func.isRequired,
    onChangeCountry: PropTypes.func.isRequired,
    onChangeZip: PropTypes.func.isRequired,
    online: PropTypes.bool.isRequired,
    originPublisherEligibility: PropTypes.object,
    originPublisherData: PropTypes.object.isRequired
  };

  state = {
    activeTab: 'address',
    editMode: false
  }

  componentDidUpdate(prevProps,_prevState) {
    this.shouldActivateEdit();
    this.shouldCheckEventEligibility(prevProps);
  }

  shouldCheckEventEligibility = (prevProps) => {
    const { venue,dispatch, category_id } = this.props;
    if (!venue || (!venue.place_id && !venue.name)) { return; }

    if ((!prevProps.venue || prevProps.venue.place_id !== venue.place_id) ||
      prevProps.category_id !==  category_id){
      dispatch(checkEventEligibility());
    }
  }

  changeTab = (tab) => {
    this.setState({ activeTab: tab });
  }

  onEdit = (status) => {
    this.setState({ editMode: status });
  }

  clearAndFocus = () => {
    const { onClearAddress} = this.props;

    this.onEdit(false);
    onClearAddress();
  }

  shouldActivateEdit = () => {
    const { venue, formErrors } = this.props;

    if (this.state.editMode) { return; }
    if (!venue || (!venue.place_id && !venue.name)) { return; }

    const isValid = !formErrors.getIn(['venue', 'name'])
      && !formErrors.getIn(['venue', 'address_1'])
      && !formErrors.getIn(['venue', 'address_2'])
      && !formErrors.getIn(['venue', 'town'])
      && !formErrors.getIn(['state_name'])
      && !formErrors.getIn(['post_code'])
      && !formErrors.getIn(['venue', 'country', 'iso_alpha2']);

    if (!isValid)
      this.onEdit(true);
  };

  render() {
    const {
      formErrors, venue, availableCountries, onVenueSelect,
      onChangeCountry, onChangeZip, online, originPublisherData,
      originPublisherEligibility
    } = this.props;

    const venueFieldVisible = !venue || (!venue.place_id && !venue.name);

    let errorMsg = null;
    if (formErrors.has('venue')) {
      if (venueFieldVisible) {
        errorMsg = online ? 'Choose a location to target a local audience.' : 'That doesn\'t look like a venue. If the venue name isn\'t searchable, try searching for the address';
      }
    }

    const searchOptions = online ? { types: ['(regions)'] } : {};
    const placeholder = online ? 'Search for a city / country' : 'Search for a venue by name or address';

    return (
      <div className="LocationFieldset">
        <div className="row">
          <div className="col">
            <FormField
              label="Virtual Event"
              error={formErrors.get('online')}
              name="online"
              component="input"
              type="checkbox"
              onChange={this.clearAndFocus}
              inputProps={ { className: styles.onlineCheck } }
              infoUrl="https://support.evvnt.com/hc/en-us/articles/360015568259-Basics-Online-Event-Venue"
            />
          </div>
        </div>
        <div className="row">
          <div className="col">
            {venueFieldVisible && <FormField
              name="venue"
              component={VenueField}
              className="form-control"
              inputProps={{ onVenueSelect, searchOptions, placeholder }}
              error={errorMsg}
            />}
          </div>
        </div>
        <VenueDetailsFieldset
          formErrors={formErrors}
          currentTab={this.state.activeTab}
          onTabChange={this.changeTab}
          venue={this.props.venue}
          onClear={this.clearAndFocus}
          editMode={this.state.editMode}
          onChangeEdit={this.changeEditMode}
          availableCountries={availableCountries}
          onEdit={this.onEdit}
          onChangeCountry={onChangeCountry}
          onChangeZip={onChangeZip}
          online={online}
          originPublisherEligibility={originPublisherEligibility}
          originPublisherData={originPublisherData}
        />
      </div>
    );
  }
}


const mapStateToProps = (state) => ({
  venue:  state.form.eventSubmission.values.venue,
  category_id: state.form.eventSubmission.values.category_id,
  originPublisherEligibility: state.publishers.get('eventEligibility'),
  originPublisherData: state.config.get('origin_publisher_data')
});

const mapDispatchToProps = (dispatch) => ({
  dispatch
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(LocationFieldset);


// WEBPACK FOOTER //
// app/client/bundles/EventSubmission/components/forms/fieldsets/LocationFieldset.jsx